/**
 * One query-string value as page state — which view is showing, which item is open — so a link
 * or a reload lands on the same thing. Read and written through the router, so its idea of the
 * location stays true; a change replaces the history entry rather than pushing one, since
 * switching a view isn't somewhere the back button should step through.
 */
import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

export function useSearchParam(name: string): [string | null, (next: string | null) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const raw = search[name];
  const value = raw === undefined || raw === null || raw === '' ? null : String(raw);
  const set = useCallback(
    (next: string | null) =>
      void navigate({
        to: '.',
        search: (previous: Record<string, unknown>) => ({ ...previous, [name]: next ?? undefined }),
        replace: true,
      } as never),
    [navigate, name],
  );
  return [value, set];
}
